import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { supabase } from '../lib/supabaseClient';
import { isCapacitorNative, isCapacitorIOS } from '../lib/platform';
import { toast } from 'sonner';

const AuthContext = createContext(null);

// Bilingual fallback for toasts fired BEFORE React mounts the I18nProvider
// (OAuth deep-link callbacks, cold starts). Reads the language preference
// the user picked the last time they were in the app.
const AUTH_TOAST = {
  fr: {
    oauth_failed:    'Connexion Apple/Google échouée : ',
    signin_failed:   'Connexion échouée : ',
    signin_success:  'Connexion réussie !',
    oauth_callback:  'Erreur de retour OAuth : ',
    unknown_error:   'erreur inconnue',
  },
  en: {
    oauth_failed:    'Apple/Google sign-in failed: ',
    signin_failed:   'Sign-in failed: ',
    signin_success:  'Signed in!',
    oauth_callback:  'OAuth callback error: ',
    unknown_error:   'unknown error',
  },
};
function authT() {
  try {
    const l = window.localStorage.getItem('lang');
    return AUTH_TOAST[l === 'en' ? 'en' : 'fr'];
  } catch { return AUTH_TOAST.fr; }
}

// Auto-logout after 30 minutes of inactivity
const INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll', 'click'];

// Custom URL scheme registered in iOS Info.plist (CFBundleURLTypes) so the
// OAuth / password-reset callback re-opens the native app instead of Safari.
// MUST be listed in Supabase → Authentication → URL Configuration → Redirect URLs.
const NATIVE_REDIRECT_URL = 'soumissionpro://auth-callback';
const WEB_RESET_PASSWORD_URL = 'https://soumissionpro.net/reset-password';
const PENDING_PASSWORD_RESET_KEY = 'sp.pendingPasswordReset';

function getOauthRedirectUrl() {
  if (isCapacitorNative()) return NATIVE_REDIRECT_URL;
  return `${window.location.origin}/auth/confirm`;
}

/** Password-reset email redirect: deep-link back into the app when native. */
function getPasswordResetRedirectUrl() {
  if (isCapacitorNative()) return NATIVE_REDIRECT_URL;
  try {
    const origin = window.location.origin || '';
    if (
      origin &&
      !origin.includes('localhost') &&
      !origin.startsWith('capacitor:') &&
      !origin.startsWith('ionic:')
    ) {
      return `${origin}/reset-password`;
    }
  } catch { /* ignore */ }
  return WEB_RESET_PASSWORD_URL;
}

function markPendingPasswordReset() {
  try { localStorage.setItem(PENDING_PASSWORD_RESET_KEY, '1'); } catch { /* ignore */ }
}

function consumePendingPasswordReset() {
  try {
    if (localStorage.getItem(PENDING_PASSWORD_RESET_KEY) === '1') {
      localStorage.removeItem(PENDING_PASSWORD_RESET_KEY);
      return true;
    }
  } catch { /* ignore */ }
  return false;
}

function goToResetPasswordScreen() {
  if (typeof window === 'undefined') return;
  if (window.location.pathname === '/reset-password') return;
  window.location.assign('/reset-password');
}

// --- (Apple Sign-In natif : voir signInWithApple) ---

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);

  const signOut = useCallback(async () => {
    // iOS Safari fix: clear local storage FIRST (synchronous) so the user is
    // immediately logged out even if the network call to Supabase hangs.
    try {
      // Local-only scope avoids the network round-trip that can hang on iOS
      // Safari (ITP, low-power mode, captive networks).
      await supabase.auth.signOut({ scope: 'local' });
    } catch (err) {
      console.warn('Local signOut failed, forcing manual cleanup:', err);
    }
    // Log out from RevenueCat on Capacitor native — anonymizes future purchases
    // on the same device until another user logs in.
    try {
      const { logoutBilling } = await import('../lib/paymentService');
      await logoutBilling();
    } catch {
      /* ignore — paymentService is web-safe */
    }
    // Always do manual cleanup as a belt-and-suspenders measure.
    // iOS Safari sometimes leaves Supabase keys behind even after signOut.
    try {
      const stores = [window.localStorage, window.sessionStorage].filter(Boolean);
      stores.forEach((store) => {
        const keys = Object.keys(store);
        keys.forEach((k) => {
          if (k.startsWith('sb-') || k.startsWith('supabase') || k.includes('-auth-token')) {
            try {
              store.removeItem(k);
            } catch {
              /* ignore */
            }
          }
        });
      });
    } catch {
      /* ignore */
    }
    // 🍎 Nettoyage des flags d'abonnement local — important quand plusieurs
    // comptes utilisent le même iPad (ex: équipe Solo) pour éviter qu'un
    // user B hérite du statut "déjà acheté" de user A.
    try {
      ['sp.lastSubStatus.v1', 'sp.iapGraceUntil.v1', 'sp.iapPurchased.v1'].forEach((k) => {
        try { window.localStorage.removeItem(k); } catch { /* ignore */ }
      });
    } catch { /* ignore */ }
    // Tell the Service Worker to nuke ALL caches so stale /api/billing/status
    // responses don't haunt the next sign-in.
    try {
      if (navigator.serviceWorker && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({ type: 'CLEAR_CACHES' });
      }
    } catch {
      /* ignore */
    }
    // Force local React state so Protected routes redirect immediately
    setSession(null);
    setUser(null);
    // Fire-and-forget global server-side revoke in the background.
    // Wrapped in setTimeout to avoid blocking the UI on iOS Safari.
    setTimeout(() => {
      supabase.auth.signOut({ scope: 'global' }).catch(() => {
        /* network may be blocked — local logout is what matters */
      });
    }, 0);
  }, []);

  // Reset (or arm) the inactivity timer
  const armInactivityTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        await supabase.auth.signOut();
        toast.info(
          (navigator.language || '').startsWith('en')
            ? 'You have been signed out after 30 minutes of inactivity.'
            : 'Vous avez été déconnecté après 30 minutes d\'inactivité.',
          { duration: 6000 }
        );
      } catch (err) {
        console.warn('Inactivity sign-out failed:', err);
      }
    }, INACTIVITY_TIMEOUT_MS);
  }, []);

  const clearInactivityTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    async function bootstrap() {
      const { data, error } = await supabase.auth.getSession();
      // Stale session from a deleted user — Supabase signals this with
      // a "User from sub claim in JWT does not exist" error. Wipe local
      // storage so the user sees the auth screen instead of a red toast.
      const msg = (error?.message || '').toLowerCase();
      if (error && (msg.includes('user from sub claim') || msg.includes('user not found') || msg.includes('does not exist'))) {
        try { await supabase.auth.signOut({ scope: 'local' }); } catch { /* noop */ }
        if (mounted) {
          setSession(null);
          setUser(null);
          setLoading(false);
        }
        return;
      }
      if (mounted) {
        setSession(data?.session || null);
        setUser(data?.session?.user || null);
        setLoading(false);
      }
    }
    bootstrap();
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      if (!mounted) return;
      setSession(s || null);
      setUser(s?.user || null);
      // Recovery session from email link → always land on the set-password screen.
      if (event === 'PASSWORD_RECOVERY') {
        markPendingPasswordReset();
        goToResetPasswordScreen();
      }
    });
    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  // Capacitor deep-link handler — OAuth + password-reset emails redirect to
  // `soumissionpro://auth-callback?...`. iOS opens the app; we exchange the
  // code and, for password reset, navigate to /reset-password inside the app.
  useEffect(() => {
    if (!isCapacitorNative()) return undefined;
    let cleanupHandle = null;
    let cancelled = false;
    async function processCallbackUrl(rawUrl) {
      if (!rawUrl || !String(rawUrl).startsWith('soumissionpro://')) return;
      const isBenignAuthError = (msg) => {
        const m = (msg || '').toLowerCase();
        return (
          m.includes('auth session missing') ||
          m.includes('user from sub claim') ||
          m.includes('user not found') ||
          m.includes('jwt expired') ||
          m.includes('refresh_token_not_found')
        );
      };
      try {
        const queryStart = rawUrl.indexOf('?');
        const hashStart = rawUrl.indexOf('#');
        const queryStr = queryStart >= 0 ? rawUrl.slice(queryStart + 1).split('#')[0] : '';
        const hashStr = hashStart >= 0 ? rawUrl.slice(hashStart + 1).split('?')[0] : '';
        const queryParams = new URLSearchParams(queryStr);
        const hashParams = new URLSearchParams(hashStr);
        const errorDescription =
          queryParams.get('error_description') || queryParams.get('error') ||
          hashParams.get('error_description') || hashParams.get('error');
        if (errorDescription) {
          if (!isBenignAuthError(errorDescription)) {
            toast.error(authT().oauth_failed + errorDescription);
          }
          return;
        }
        const type = queryParams.get('type') || hashParams.get('type') || '';
        const isRecovery =
          type === 'recovery' ||
          String(rawUrl).includes('reset-password') ||
          localStorage.getItem(PENDING_PASSWORD_RESET_KEY) === '1';

        const finishWithSession = (session) => {
          if (!session) return;
          setSession(session);
          setUser(session.user || null);
          if (isRecovery || consumePendingPasswordReset()) {
            goToResetPasswordScreen();
            return;
          }
          toast.success(authT().signin_success);
        };

        const code = queryParams.get('code') || hashParams.get('code');
        if (code) {
          const { data, error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) {
            if (!isBenignAuthError(error.message)) {
              toast.error(authT().signin_failed + (error.message || authT().unknown_error));
            }
            return;
          }
          finishWithSession(data?.session);
          return;
        }

        const tokenHash = queryParams.get('token_hash') || hashParams.get('token_hash');
        if (tokenHash && (type === 'recovery' || isRecovery)) {
          const { data, error } = await supabase.auth.verifyOtp({
            token_hash: tokenHash,
            type: 'recovery',
          });
          if (error) {
            if (!isBenignAuthError(error.message)) {
              toast.error(authT().signin_failed + (error.message || authT().unknown_error));
            }
            return;
          }
          finishWithSession(data?.session);
          return;
        }

        const accessToken = hashParams.get('access_token') || queryParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token') || queryParams.get('refresh_token');
        if (accessToken && refreshToken) {
          const { data, error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (error) {
            if (!isBenignAuthError(error.message)) {
              toast.error(authT().signin_failed + (error.message || authT().unknown_error));
            }
            return;
          }
          finishWithSession(data?.session);
          return;
        }
        // No code, no token, no error — likely a stale callback after sign-out.
      } catch (err) {
        if (!isBenignAuthError(err?.message)) {
          toast.error(authT().oauth_callback + (err?.message || String(err)));
        }
      }
    }
    (async () => {
      try {
        const { App } = await import('@capacitor/app');
        // 1) Cold-start case: the app was launched FROM the deep link, so
        //    iOS already dispatched the URL before our listener was ready.
        //    Replay it via getLaunchUrl().
        try {
          const launch = await App.getLaunchUrl();
          if (launch?.url) {
            await processCallbackUrl(launch.url);
          }
        } catch { /* getLaunchUrl unsupported on web — ignore */ }
        // 2) Warm-start case: the app was already running, register a
        //    listener for any future deep-link URL.
        const handle = await App.addListener('appUrlOpen', async (event) => {
          await processCallbackUrl(event?.url || '');
        });
        if (cancelled) {
          try { handle.remove(); } catch { /* noop */ }
        } else {
          cleanupHandle = handle;
        }
      } catch (err) {
        // @capacitor/app not available (e.g. pure-web bundle) — silently ignore.
        console.warn('[AuthContext] @capacitor/app listener not registered:', err?.message);
      }
    })();
    return () => {
      cancelled = true;
      try { cleanupHandle?.remove?.(); } catch { /* noop */ }
    };
  }, []);

  // Inactivity watchdog: only active when there is a session
  useEffect(() => {
    if (!session) {
      clearInactivityTimer();
      return undefined;
    }
    armInactivityTimer();
    const onActivity = () => armInactivityTimer();
    ACTIVITY_EVENTS.forEach((ev) =>
      window.addEventListener(ev, onActivity, { passive: true })
    );
    return () => {
      ACTIVITY_EVENTS.forEach((ev) =>
        window.removeEventListener(ev, onActivity)
      );
      clearInactivityTimer();
    };
  }, [session, armInactivityTimer, clearInactivityTimer]);

  const signIn = (email, password) =>
    supabase.auth.signInWithPassword({ email, password });

  const signUp = (email, password) =>
    supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: `${window.location.origin}/auth/confirm` },
    });

  const resendConfirmation = (email) =>
    supabase.auth.resend({
      type: 'signup',
      email,
      options: { emailRedirectTo: `${window.location.origin}/auth/confirm` },
    });

  const resetPassword = (email) => {
    // From the native app: redirect must be the custom scheme so the email
    // link re-opens SoumissionPro instead of the mobile website in Safari.
    if (isCapacitorNative()) markPendingPasswordReset();
    return supabase.auth.resetPasswordForEmail(email, {
      redirectTo: getPasswordResetRedirectUrl(),
    });
  };

  const signInWithGoogle = () => {
    if (isCapacitorIOS()) {
      return Promise.resolve({
        error: { message: 'Google Sign-In is not available in the iOS app.' },
      });
    }
    return supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: getOauthRedirectUrl() },
    });
  };

  // Apple Sign-In :
  // - iOS natif (Capacitor) → plugin Apple natif (obligatoire sur iPad:
  //   presentationContextProvider patché via scripts/patch-apple-sign-in-ios.sh)
  // - Web → OAuth Supabase classique
  const signInWithApple = async () => {
    if (!isCapacitorIOS()) {
      return supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: { redirectTo: getOauthRedirectUrl() },
      });
    }

    try {
      const { SignInWithApple } = await import('@capacitor-community/apple-sign-in');

      const result = await SignInWithApple.authorize({
        clientId: 'ca.soumissionpro.app',
        redirectURI: 'https://soumissionpro.net/auth/confirm',
        scopes: 'email name',
        state: 'soumissionpro-' + Date.now(),
      });

      const resp = result?.response;
      const idToken = resp?.identityToken;
      if (!idToken) {
        return { error: { message: 'Apple n\'a pas retourné de jeton d\'identité.' } };
      }

      // Pas de nonce : GoTrue compare hex(SHA256) vs base64url(SHA256) d'Apple
      // → "Nonces mismatch" sur Supabase hosted (supabase/auth#2378).
      const { data, error } = await supabase.auth.signInWithIdToken({
        provider: 'apple',
        token: idToken,
      });

      if (error) return { error };

      if (data?.session) {
        setSession(data.session);
        setUser(data.session.user || null);
      }

      const given = resp?.givenName;
      const family = resp?.familyName;
      if ((given || family) && data?.user) {
        const fullName = [given, family].filter(Boolean).join(' ');
        supabase.auth.updateUser({
          data: { full_name: fullName, given_name: given, family_name: family },
        }).catch(() => {});
      }

      return { data };
    } catch (err) {
      const msg = (err?.message || '').toLowerCase();
      const code = err?.code || err?.errorCode;
      const cancelled =
        msg.includes('canceled') ||
        msg.includes('cancelled') ||
        msg.includes('user canceled') ||
        code === '1001' ||
        code === 1001 ||
        code === 'ERR_CANCELED';
      if (cancelled) {
        return { error: null, cancelled: true };
      }
      return { error: { message: err?.message || 'Connexion Apple échouée.' } };
    }
  };

  // Push a session directly into React state from a callback (e.g. AuthConfirm
  // after exchangeCodeForSession). Avoids race condition where Protected routes
  // evaluate `user` before onAuthStateChange has fired.
  const setSessionFromExchange = useCallback((newSession) => {
    setSession(newSession || null);
    setUser(newSession?.user || null);
  }, []);

  const value = React.useMemo(
    () => ({ session, user, loading, signIn, signUp, signOut, resetPassword, signInWithGoogle, signInWithApple, resendConfirmation, setSessionFromExchange }),
    [session, user, loading, signOut, setSessionFromExchange]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
